<template>
  <div>
    <form @submit.prevent="submit" class="text-left default-form">
      <div class="form-group mb-3">
        <label class="form-label fw-bold required">{{ $t('name') }}</label>
        <input type="text" class="form-control" :placeholder="$t('name')" v-model.trim="form.name" />
      </div>
      <div class="form-group mb-3" v-if="Array.isArray(type) && type.length > 1">
        <label class="form-label fw-bold required">{{ $t('type') }}</label>
        <select class="form-control form-select" v-model.trim="form.type">
          <option value="SITE_LIST">{{ $t('custom_targeting_type.site_list') }}</option>
          <option value="APP_LIST">{{ $t('custom_targeting_type.app_list') }}</option>
        </select>
      </div>
      <div class="form-group mb-3" v-if="form.type">
        <label class="form-label fw-bold">{{ valuesLabel }}</label>
        <p class="font-size-xSmall">{{ valuesDescription }}</p>
        <b-form-tags
          :input-id="`tags-separators-${targeting ? targeting.id : ''}`"
          v-model="form.values"
          :separator="separator"
          :placeholder="$t('enter2')"
          remove-on-delete
        ></b-form-tags>
      </div>
      <div class="form-group mb-3" v-if="form.type">
        <label class="form-label fw-bold">{{ $t('or_upload_the_spreadsheet') }}</label><br/>
        <p class="font-size-xSmall">{{ $t('add_1st_party') }}<br>
          <a v-if="type === 'EMAIL_AUDIENCE'" href="/models/planilha-modelo-audiencias.csv" class="font-size-small text-decoration-underline me-3" target="_blank" download><i class="fa fa-paperclip" aria-hidden="true"></i> {{ $t('audience_template') }} CSV</a>
          <a v-if="type === 'EMAIL_AUDIENCE'" href="/models/planilha-modelo-audiencias.xlsx" class="font-size-small text-decoration-underline" target="_blank" download><i class="fa fa-paperclip" aria-hidden="true"></i> {{ $t('audience_template') }} XLSX</a>
          <a v-if="type === 'SITE_LIST'" href="/models/planilha-modelo-sites.csv" class="font-size-small text-decoration-underline me-3" target="_blank" download><i class="fa fa-paperclip" aria-hidden="true"></i> {{ $t('website_template') }} CSV</a>
          <a v-if="type === 'SITE_LIST'" href="/models/planilha-modelo-sites.xlsx" class="font-size-small text-decoration-underline" target="_blank" download><i class="fa fa-paperclip" aria-hidden="true"></i> {{ $t('website_template') }} XLSX</a>
          <a v-if="type === 'APP_LIST'" href="/models/planilha-modelo-apps.csv" class="font-size-small text-decoration-underline me-3" target="_blank" download><i class="fa fa-paperclip" aria-hidden="true"></i> {{ $t('app_template') }} CSV</a>
          <a v-if="type === 'APP_LIST'" href="/models/planilha-modelo-apps.xlsx" class="font-size-small text-decoration-underline" target="_blank" download><i class="fa fa-paperclip" aria-hidden="true"></i> {{ $t('app_template') }} XLSX</a>
        </p>
        <input type="file" @change="changeFile" accept=".csv, .xlsx" />
        <p class="font-size-xSmall">{{ $t('CSV_or_XLSX_files_only') }}</p>
      </div>
      <div class="text-center mt-3">
        <!-- MESSAGES -->
        <div
          class="mb-2 alert-message-card text-color2 font-size-small px-3 py-2"
          v-if="loader"
        >
          <p
            class="mb-0"
          >
            {{ $t('this_action_may_take_a_few_minutes') }}
          </p>
        </div> <!-- END MESSAGES -->
        <Button
          type="submit"
          class="btn btn-color10"
          :loading="loader"
        >
          {{ targeting ? $t('save') : $t('create') }}
        </Button>
      </div>
    </form>
  </div>
</template>

<script>

import CustomTargetingsService from '@/modules/custom-targetings/services/custom-targetings-service'

import Button from '@/components/common/Button/Button'

export default {
  name: 'UpsertCustomTargetingForm',
  props: ['advertiser', 'targeting', 'type', 'values'],
  components: {
    Button
  },
  data () {
    return {
      loader: false,
      form: {
        advertiser_id: '',
        name: '',
        type: '',
        values: [],
        file: ''
      }
    }
  },
  created () {
    if (this.advertiser) this.form.advertiser_id = this.advertiser.id
    if (this.targeting) this.form.name = this.targeting.name
    if (this.type && !Array.isArray(this.type)) this.form.type = this.type
    if (this.values) this.form.values = this.values
  },
  computed: {
    separator () {
      switch (this.form.type) {
        case 'APP_LIST':
          return ',;'
        case 'EMAIL_AUDIENCE':
        case 'SITE_LIST':
          return ' ,;'
        default:
          return ','
      }
    },
    /**
     * Values description
     */
    valuesDescription () {
      switch (this.form.type) {
        case 'EMAIL_AUDIENCE':
          return this.$t('separate_email_addresses')
        case 'SITE_LIST':
          return this.$t('separate_website_addresses')
        case 'APP_LIST':
          return this.$t('separate_app_addresses')
        default:
          return ''
      }
    },
    /**
     * Values label
     */
    valuesLabel () {
      switch (this.form.type) {
        case 'EMAIL_AUDIENCE':
          return this.$t('email_addresses')
        case 'SITE_LIST':
          return this.$t('website_addresses')
        case 'APP_LIST':
          return this.$t('app_addresses')
        default:
          return ''
      }
    }
  },
  methods: {
    /**
     * Handle change on file
     */
    changeFile (e) {
      const file = e.target.files[0]
      this.form.file = file
    },
    /**
     * Submit
     */
    async submit () {
      this.loader = true
      try {
        let targeting = null

        if (this.targeting) {
          targeting = await CustomTargetingsService.updateCustomTargeting(this.targeting.id, {
            name: this.form.name,
            values: this.form.values,
            file: this.form.file
          })
          this.$emit('custom-targeting-updated', targeting)
        } else {
          targeting = await CustomTargetingsService.createCustomTargeting({
            advertiserId: this.form.advertiser_id,
            name: this.form.name,
            type: this.form.type,
            values: this.form.values,
            file: this.form.file
          })
          this.$emit('custom-targeting-created', targeting)
        }
      } catch (error) {
        this.$emit('upsert-custom-targeting-error', error)
      }
      this.loader = false
    }
  }
}
</script>

<style lang="scss" scoped>
::v-deep {
  .b-form-tag-remove {
    padding: 0;
    background-color: transparent;
    border: 0;
    float: right;
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1;
    color: #fff;
    text-shadow: 0 1px 0 #fff;
    opacity: .5;
  }
  .badge-secondary {
    color: #fff;
    background-color: $color9;
  }

  .b-form-tags-button {
    display: none;
  }
}
.b-form-tags-list {
  margin-top: -.25rem;
}
.b-form-tags-list .b-form-tag, .b-form-tags-list .b-form-tags-field {
  margin-top: .25rem;
}
.b-form-tag {
  font-size: 75%;
  font-weight: 400;
  line-height: 1.5;
  margin-right: .25rem;
}
.mw-100 {
  max-width: 100% !important;
}
.b-form-tag > button.b-form-tag-remove {
  color: inherit;
  font-size: 125%;
  line-height: 1;
  float: none;
  margin-left: .25rem;
}
</style>
